import type { InboxItemId, InboxState, EpochMs, User, PresenceState, ChannelId } from '../../types';

import { fanoutLast } from '../fanout';
import { removeFromIndex } from '../indexes';
import { richDocFrom, richDocToText } from '../../rich-doc';
import { asRecord, snapshot } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';
import { synced } from '../sync-hook';

export function inboxMarkRead(ids: InboxItemId[]): void {
  const cache = requireCache();
  const now = cache.nowMs();
  for (const id of ids.slice(0, 200)) {
    const item = cache.inbox.get(id);
    if (item === undefined) {
      continue;
    }
    const before = { state: item.state, stateChangedAt: item.stateChangedAt };
    item.state = 'read';
    item.stateChangedAt = now;
    logActivity(cache, {
      mutator: 'inbox.markRead',
      entityTable: 'inboxItem',
      entityId: String(item.id),
      op: 'update',
      before,
      after: { state: 'read', stateChangedAt: now },
    });
  }
  bump('inbox');
}

export function inboxMarkUnread(ids: InboxItemId[]): void {
  const cache = requireCache();
  const now = cache.nowMs();
  for (const id of ids) {
    const item = cache.inbox.get(id);
    if (item === undefined) {
      continue;
    }
    const before = { state: item.state, stateChangedAt: item.stateChangedAt };
    item.state = 'unread';
    item.stateChangedAt = now;
    logActivity(cache, {
      mutator: 'inbox.markUnread',
      entityTable: 'inboxItem',
      entityId: String(item.id),
      op: 'update',
      before,
      after: { state: 'unread', stateChangedAt: now },
    });
  }
  bump('inbox');
}

export function inboxArchive(ids: InboxItemId[]): () => void {
  const cache = requireCache();
  const now = cache.nowMs();
  const previous: Array<{ id: InboxItemId; state: InboxState }> = [];
  for (const id of ids) {
    const item = cache.inbox.get(id);
    if (item === undefined) {
      continue;
    }
    previous.push({ id, state: item.state });
    const before = { state: item.state, stateChangedAt: item.stateChangedAt };
    item.state = 'archived';
    item.stateChangedAt = now;
    logActivity(cache, {
      mutator: 'inbox.archive',
      entityTable: 'inboxItem',
      entityId: String(item.id),
      op: 'update',
      before,
      after: { state: 'archived', stateChangedAt: now },
    });
  }
  bump('inbox');
  return () => restoreInbox(previous);
}

export function inboxRestore(rows: Array<{ id: InboxItemId; state: InboxState }>): void {
  const cache = requireCache();
  const at = cache.nowMs();
  for (const row of rows) {
    const item = cache.inbox.get(row.id);
    if (item !== undefined) {
      item.state = row.state;
      item.stateChangedAt = at;
    }
  }
  bump('inbox');
}

const restoreInbox = synced('inboxRestore', 'replicated', inboxRestore);

export function inboxDelete(id: InboxItemId): void {
  const cache = requireCache();
  const item = cache.inbox.get(id);
  if (item === undefined || item.state !== 'archived') {
    return;
  }
  removeFromIndex(cache, 'inboxItem', String(item.id));
  cache.inbox.delete(item.id);
  logActivity(cache, {
    mutator: 'inbox.delete',
    entityTable: 'inboxItem',
    entityId: String(item.id),
    op: 'delete',
    before: snapshot(item),
    after: null,
  });
  fanoutLast(cache);
  bump('inbox');
}

export function inboxSnooze(ids: InboxItemId[], untilAt: EpochMs): void {
  const cache = requireCache();
  const now = cache.nowMs();
  for (const id of ids) {
    const item = cache.inbox.get(id);
    if (item === undefined) {
      continue;
    }
    const before = { snoozedUntilAt: item.snoozedUntilAt, state: item.state, stateChangedAt: item.stateChangedAt };
    item.snoozedUntilAt = untilAt;
    item.state = 'read';
    item.stateChangedAt = now;
    logActivity(cache, {
      mutator: 'inbox.snooze',
      entityTable: 'inboxItem',
      entityId: String(item.id),
      op: 'update',
      before,
      after: { snoozedUntilAt: untilAt, state: 'read', stateChangedAt: now },
    });
  }
  bump('inbox');
}

export function updateProfile(patch: Partial<Omit<User, 'id' | 'kind'>>): void {
  const cache = requireCache();
  const user = cache.users.get(cache.me);
  if (user === undefined) {
    throw new Error('Свой пользователь не найден');
  }
  const aboutJson = patch.aboutJson === undefined || patch.aboutJson === null ? patch.aboutJson : richDocFrom(patch.aboutJson);
  const fullPatch: Partial<Omit<User, 'id' | 'kind'>> =
    aboutJson !== undefined
      ? { ...patch, aboutJson, about: aboutJson === null ? null : richDocToText(aboutJson) }
      : patch;
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  const userRecord = asRecord(user);
  for (const [key, value] of Object.entries(fullPatch)) {
    if (key === 'id' || key === 'kind') {
      continue;
    }
    if (value !== undefined) {
      before[key] = userRecord[key];
      after[key] = value;
    }
  }
  Object.assign(user, after);
  logActivity(cache, {
    mutator: 'profile.update',
    entityTable: 'user',
    entityId: String(user.id),
    op: 'update',
    before,
    after,
  });
  fanoutLast(cache);
  bump('presence');
}

export function setPresence(args: {
  state: PresenceState;
  statusEmoji?: string | null;
  statusText?: string | null;
  statusUntilAt?: EpochMs | null;
}): void {
  const cache = requireCache();
  const now = cache.nowMs();
  let presence = cache.presence.get(cache.me);
  if (presence === undefined) {
    presence = {
      userId: cache.me,
      state: args.state,
      statusEmoji: null,
      statusText: null,
      statusUntilAt: null,
      lastSeenAt: now,
      typingInChannelId: null,
    };
    cache.presence.set(cache.me, presence);
    logActivity(cache, {
      mutator: 'presence.set',
      entityTable: 'user',
      entityId: String(cache.me),
      op: 'insert',
      before: null,
      after: snapshot(presence),
    });
  } else {
    const before = {
      state: presence.state,
      statusEmoji: presence.statusEmoji,
      statusText: presence.statusText,
      statusUntilAt: presence.statusUntilAt,
      lastSeenAt: presence.lastSeenAt,
    };
    presence.state = args.state;
    if (args.statusEmoji !== undefined) {
      presence.statusEmoji = args.statusEmoji;
    }
    if (args.statusText !== undefined) {
      presence.statusText = args.statusText;
    }
    if (args.statusUntilAt !== undefined) {
      presence.statusUntilAt = args.statusUntilAt;
    }
    presence.lastSeenAt = now;
    logActivity(cache, {
      mutator: 'presence.set',
      entityTable: 'user',
      entityId: String(cache.me),
      op: 'update',
      before,
      after: {
        state: presence.state,
        statusEmoji: presence.statusEmoji,
        statusText: presence.statusText,
        statusUntilAt: presence.statusUntilAt,
        lastSeenAt: presence.lastSeenAt,
      },
    });
  }
  fanoutLast(cache);
  bump('presence');
}

export function setTyping(channelId: ChannelId | null): void {
  const cache = requireCache();
  const presence = cache.presence.get(cache.me);
  if (presence === undefined) {
    throw new Error('Строка присутствия не найдена');
  }
  const before = { typingInChannelId: presence.typingInChannelId };
  presence.typingInChannelId = channelId;
  logActivity(cache, {
    mutator: 'presence.typing',
    entityTable: 'user',
    entityId: String(cache.me),
    op: 'update',
    before,
    after: { typingInChannelId: presence.typingInChannelId },
  });
  fanoutLast(cache);
  bump('presence');
}

export function setFilter(patch: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === undefined || value === '') {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, String(value));
    }
  }
  window.history.replaceState(null, '', url);
}

export function resetFilters(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  const layout = url.searchParams.get('layout');
  url.search = '';
  if (layout !== null) {
    url.searchParams.set('layout', layout);
  }
  window.history.replaceState(null, '', url);
}
